import { SecureStateType } from '../constants/enums'
import { getImageSecurityTokens } from '../mfcLogic/api/imageSecurity'
import { updateSecureState } from '../state/state.secure'
import { registerTimer, unregisterTimer } from '../state/state.timers'

const REFRESH_AFTER_MS = 60 * 60 * 1000
const CHECK_EVERY_MS = 60 * 1000

/**
 * Snaps tokens read `<userId>/<expiresAtSeconds>/<signature>`. One is used
 * until it expires, and for at least two hours after its request, past the
 * next refetch, so a device clock running ahead of the server's cannot
 * reject a fresh token. One without a readable expiry is always used.
 */
const usableUntil = (token: string, requestedAt: number) => {
    const expiresAt = Number(token.split('/')[1]) * 1000
    if (!(expiresAt > 0)) return Infinity
    return Math.max(expiresAt, requestedAt + 2 * REFRESH_AFTER_MS)
}

let stopRefresh: (() => void) | undefined

export const stopImageSecurityRefresh = () => {
    stopRefresh?.()
    stopRefresh = undefined
}

/**
 * Loads the image security tokens, then refetches them an hour after each
 * request.
 *
 * The schedule is kept on the wall clock and the timer only samples it:
 * timers stop while a page is suspended in the background or the device
 * sleeps, so an hourly timer can wake holding a token that expired in the
 * meantime. A page that turns visible checks at once instead of waiting for
 * the next sample.
 */
export const startImageSecurityRefresh = (forceBlur: boolean) => {
    stopImageSecurityRefresh()

    let fetching = false
    let nextFetchAt = 0
    let failures = 0

    const fetchIfDue = () => {
        const requestedAt = Date.now()
        if (fetching || requestedAt < nextFetchAt) return
        fetching = true
        void getImageSecurityTokens(forceBlur).then((config) => {
            fetching = false
            const token = config?.snaps?.token
            if (!token) {
                // a failed fetch settles without a token: keep the current
                // one, retry on the next check, then back off to hourly
                nextFetchAt =
                    requestedAt +
                    Math.min(
                        CHECK_EVERY_MS * (2 ** failures++ - 1),
                        REFRESH_AFTER_MS,
                    )
                return
            }
            failures = 0
            nextFetchAt = requestedAt + REFRESH_AFTER_MS
            updateSecureState(SecureStateType.IMAGE_SECURITY_CONFIG, {
                ...config,
                snaps: { token, usableUntil: usableUntil(token, requestedAt) },
            })
        })
    }
    const onVisibilityChange = () => {
        if (!document.hidden) fetchIfDue()
    }

    fetchIfDue()
    registerTimer('imageSecurity', fetchIfDue, CHECK_EVERY_MS)
    document.addEventListener('visibilitychange', onVisibilityChange)

    stopRefresh = () => {
        unregisterTimer('imageSecurity')
        document.removeEventListener('visibilitychange', onVisibilityChange)
    }
}
